import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import BelgiumHero from './components/BelgiumHero';
import BelgiumContents from './components/BelgiumContents';
import BelgiumClauses from './components/BelgiumClauses';
import BelgiumFaq from './components/BelgiumFaq';
import BelgiumInternalLinks from './components/BelgiumInternalLinks';

const faqs = [
  {
    question: 'Wat is een freelance contract in België?',
    answer: 'Een freelance contract in België is een juridisch bindende overeenkomst tussen een zelfstandige (freelancer) en een opdrachtgever. In België wordt dit ook wel een "aannemingsovereenkomst" of "overeenkomst van opdracht" genoemd, geregeld door het Belgisch Burgerlijk Wetboek.',
  },
  {
    question: 'Heb ik een ondernemingsnummer nodig als freelancer in België?',
    answer: 'Ja, als freelancer in België moet je ingeschreven zijn bij de Kruispuntbank van Ondernemingen (KBO) en beschik je over een ondernemingsnummer (BE + 10 cijfers). Dit nummer moet vermeld worden op al je facturen en contracten.',
  },
  {
    question: 'Welk recht is van toepassing op freelance contracten in België?',
    answer: 'Op freelance contracten in België is het Belgisch recht van toepassing, meer bepaald het Belgisch Burgerlijk Wetboek. Bij geschillen zijn de Belgische rechtbanken bevoegd.',
  },
  {
    question: 'Is een freelance contract verplicht in België?',
    answer: 'Een schriftelijk freelance contract is niet wettelijk verplicht in België, maar het is sterk aanbevolen. Zonder contract is het moeilijk om afspraken te bewijzen bij een geschil.',
  },
  {
    question: 'Kan ik een gratis freelance contract template gebruiken in België?',
    answer: 'Ja, met Pactorae kun je gratis een professioneel freelance contract genereren dat is afgestemd op de Belgische wetgeving. De generator verwerkt automatisch je ondernemingsnummer, btw-nummer en de correcte Belgische juridische bepalingen.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/freelance-contract-belgium',
      url: 'https://pactorae.com/freelance-contract-belgium',
      name: 'Freelance Contract Template België | Pactorae',
      description: 'Gratis freelance contract template voor Belgische freelancers en zelfstandigen. Met ondernemingsnummer, btw-nummer en conform Belgisch recht.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: { '@type': 'Answer', text: faq?.answer },
      })),
    },
  ],
};

const FreelanceContractBelgiumPage = () => (
  <>
    <Helmet>
      <html lang="nl" />
      <title>Freelance Contract Template België | Pactorae</title>
      <meta
        name="description"
        content="Gratis freelance contract template voor Belgische freelancers en zelfstandigen. Met ondernemingsnummer, btw-nummer en conform Belgisch recht. Genereer direct online via Pactorae."
      />
      <meta
        name="keywords"
        content="freelance contract België, zelfstandige overeenkomst België, ondernemingsnummer contract, btw-nummer freelancer, freelance template België, contract generator België"
      />
      <link rel="canonical" href="https://pactorae.com/freelance-contract-belgium" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://pactorae.com/freelance-contract-belgium" />
      <meta property="og:title" content="Freelance Contract Template België | Pactorae" />
      <meta
        property="og:description"
        content="Gratis freelance contract template voor Belgische freelancers. Met ondernemingsnummer, btw-nummer en conform Belgisch recht."
      />
      <meta property="og:site_name" content="Pactorae" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Freelance Contract Template België | Pactorae" />
      <meta
        name="twitter:description"
        content="Gratis freelance contract template voor Belgische freelancers. Met ondernemingsnummer, btw-nummer en conform Belgisch recht."
      />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>
    <main>
      <BelgiumHero />
      <article className="max-w-4xl mx-auto px-6 py-16">
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Freelance contract voor Belgische zelfstandigen</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            Als freelancer of zelfstandige in België heb je een professioneel contract nodig dat voldoet aan de Belgische wetgeving. Pactorae genereert in 30 seconden een op maat gemaakt freelance contract met jouw ondernemingsnummer, btw-nummer en alle vereiste Belgische juridische bepalingen.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Of je nu als IT-consultant, grafisch ontwerper, marketeer of andere zelfstandige werkt — ons contract beschermt je bij onbetaalde facturen, scope creep en juridische conflicten. Volledig conform het Belgisch Burgerlijk Wetboek en de Belgische btw-wetgeving.
          </p>
        </section>
        <BelgiumContents />
        <BelgiumClauses />
        <BelgiumFaq />
        <BelgiumInternalLinks />
        {/* Final CTA */}
        <section className="text-center py-12 bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl px-8">
          <h2 className="text-2xl font-bold text-white mb-4">Genereer jouw Belgisch freelance contract</h2>
          <p className="text-blue-100 mb-8 max-w-lg mx-auto">
            Klaar in 30 seconden. Gratis. Juridisch correct voor België.
          </p>
          <Link
            to="/contract-generator"
            className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-10 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
          >
            Genereer jouw contract gratis
          </Link>
        </section>
      </article>
    </main>
  </>
);

export default FreelanceContractBelgiumPage;
